import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const GOLD = '#C9A84C';
const NAV_PAD_Y = { mobile: '1.25rem', desktop: '1.5rem' };
const LOGO_SIZE = '2.5rem';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const mobileMenuRef = useRef(null);
  const mobileMenuToggleRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const nav = document.getElementById('dd-main-navbar');
    if (!nav) return undefined;

    const syncNavbarHeight = () => {
      const height = Math.ceil(nav.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--dd-navbar-h', `${height}px`);
    };

    syncNavbarHeight();
    const observer = new ResizeObserver(syncNavbarHeight);
    observer.observe(nav);
    window.addEventListener('resize', syncNavbarHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', syncNavbarHeight);
    };
  }, [isMobile, isOpen]);

  useEffect(() => {
    if (!isOpen || !isMobile) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, isMobile]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  const mainNavItems = [
    { name: 'Home', path: '/' },
    { name: 'The Experience', path: '/packages' },
    { name: 'Catalog', path: '/Catalog' },
    { name: 'About', path: '/about' },
  ];

  const mobileNavItems = [
    ...mainNavItems,
    { name: 'Find Booking', path: '/find-booking' },
  ];

  const isActive = (path) => {
    if (path.toLowerCase() === '/catalog') {
      return location.pathname.toLowerCase() === '/catalog';
    }
    return location.pathname === path;
  };

  const getNavLabel = (name) => {
    if (!isMobile) return name;
    if (name === 'The Experience') return 'Experience';
    return name;
  };

  const navLinkStyle = (path) => ({
    color: isActive(path) ? GOLD : '#F5F0E8',
    textDecoration: 'none',
    fontWeight: isActive(path) ? '600' : '500',
    padding: isMobile ? '0.35rem 0.35rem 0.45rem' : '0.375rem 0.5rem 0.5rem',
    borderRadius: 0,
    backgroundColor: 'transparent',
    borderBottom: isActive(path) ? `2px solid ${GOLD}` : '2px solid transparent',
    whiteSpace: 'nowrap',
    fontSize: isMobile ? '0.8125rem' : 'clamp(0.875rem, 2vw, 1rem)',
    flexShrink: 0,
  });

  const closeMobileMenu = () => setIsOpen(false);
  const handleMainMenuToggle = () => setIsOpen((prev) => !prev);

  return (
    <>
      <nav
        id="dd-main-navbar"
        style={{
          backgroundColor: '#000000',
          padding: isMobile ? `${NAV_PAD_Y.mobile} 1rem` : `${NAV_PAD_Y.desktop} clamp(1rem, 4vw, 2rem)`,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          borderBottom: '1px solid rgba(201, 168, 76, 0.2)',
        }}
      >
        <div
          style={{
            display: isMobile ? 'flex' : 'grid',
            gridTemplateColumns: isMobile ? undefined : '1fr auto 1fr',
            alignItems: 'center',
            justifyContent: isMobile ? 'space-between' : undefined,
            padding: isMobile ? 0 : '0 1rem',
            flexWrap: 'nowrap',
            gap: isMobile ? '0.625rem' : '0.5rem',
          }}
        >
          {/* Logo — left corner, original markup */}
          {isMobile ? (
            <>
              <Link
                to="/"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  flexShrink: 0,
                }}
              >
                <img
                  src="/logo.webp"
                  alt="Digital Divot Logo"
                  style={{
                    width: LOGO_SIZE,
                    height: LOGO_SIZE,
                    objectFit: 'contain',
                    border: 'none',
                    outline: 'none',
                  }}
                />
              </Link>
              <Link
                to="/"
                className="dd-mobile-brand-name"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textDecoration: 'none',
                  color: GOLD,
                  fontWeight: '700',
                  fontSize: '1rem',
                  letterSpacing: '0.03em',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Digital Divot
              </Link>
            </>
          ) : (
            <Link
              to="/"
              style={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                color: '#F5F0E8',
                fontWeight: 'bold',
                minWidth: 'fit-content',
                flexShrink: 0,
                justifySelf: 'start',
              }}
            >
              <img
                src="/logo.webp"
                alt="Digital Divot Logo"
                style={{
                  width: LOGO_SIZE,
                  height: LOGO_SIZE,
                  marginRight: '0.5rem',
                  objectFit: 'contain',
                  flexShrink: 0,
                  border: 'none',
                  outline: 'none',
                }}
              />
              <span
                style={{
                  whiteSpace: 'nowrap',
                  fontSize: 'clamp(0.875rem, 2.5vw, 1rem)',
                }}
              >
                Digital Divot
              </span>
            </Link>
          )}

          {/* Center — 3 main pages */}
          {!isMobile && (
            <div
              className="dd-nav-links"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                flexWrap: 'nowrap',
                justifyContent: 'center',
                justifySelf: 'center',
              }}
            >
              {mainNavItems.map((item) => (
                <Link key={item.name} to={item.path} style={navLinkStyle(item.path)}>
                  {getNavLabel(item.name)}
                </Link>
              ))}
            </div>
          )}

          {/* Right — Find Booking + Book Now */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '0.625rem' : '0.75rem',
              flexShrink: 0,
              justifySelf: 'end',
            }}
          >
            {!isMobile && (
              <Link to="/find-booking" style={navLinkStyle('/find-booking')}>
                Find Booking
              </Link>
            )}

            <Link
              to="/booking"
              className="dd-nav-book-btn"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: GOLD,
                color: '#000000',
                padding: isMobile ? '0.5rem 1.125rem' : '0.625rem 1.375rem',
                borderRadius: '9999px',
                textDecoration: 'none',
                fontWeight: '700',
                whiteSpace: 'nowrap',
                fontSize: isMobile ? '0.8125rem' : '0.9rem',
                lineHeight: 1,
                letterSpacing: '0.02em',
                border: 'none',
                boxSizing: 'border-box',
                flexShrink: 0,
                boxShadow: '0 2px 10px rgba(201, 168, 76, 0.35)',
              }}
            >
              {isMobile ? 'Book' : 'Book Now'}
            </Link>

            {isMobile && (
              <button
                ref={mobileMenuToggleRef}
                type="button"
                className="dd-mobile-menu-toggle"
                onClick={handleMainMenuToggle}
                aria-expanded={isOpen}
                aria-label={isOpen ? 'Close menu' : 'Open menu'}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#F5F0E8',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0.375rem',
                  minHeight: 'auto',
                  minWidth: 'auto',
                }}
              >
                {isOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            )}
          </div>
        </div>
      </nav>

      {isMobile && isOpen && (
        <>
          <button
            type="button"
            className="dd-mobile-backdrop"
            aria-label="Close menu"
            onClick={closeMobileMenu}
          />
          <div
            ref={mobileMenuRef}
            className="dd-mobile-menu-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
          >
            <div className="dd-mobile-menu-header">
              <span style={{ color: GOLD, fontWeight: '700', fontSize: '0.9rem' }}>Menu</span>
              <button
                type="button"
                onClick={closeMobileMenu}
                aria-label="Close menu"
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#F5F0E8',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.25rem',
                  minHeight: 'auto',
                  minWidth: 'auto',
                }}
              >
                <X size={22} />
              </button>
            </div>

            <nav className="dd-mobile-menu-nav" aria-label="Main">
              {mobileNavItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`dd-mobile-nav-link${isActive(item.path) ? ' dd-mobile-nav-link--active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>
        </>
      )}
    </>
  );
};

export default Navbar;
